package de.oevbs.kunde.kundenportal2.services.registration.store

import de.oevbs.kunde.kundenportal2.Pages
import de.oevbs.kunde.kundenportal2.RegistrationStep
import de.oevbs.kunde.kundenportal2.components.GlobalServiceMessagesStore
import de.oevbs.kunde.kundenportal2.components.globalSubmitTracker
import de.oevbs.kunde.kundenportal2.model.services.registration.RegistrationUser
import de.oevbs.kunde.kundenportal2.navigation.visit
import de.oevbs.kunde.kundenportal2.redirectDelay
import de.oevbs.kunde.kundenportal2.services.registration.RegistrationClient
import de.oevbs.kunde.kundenportal2.util.tryCatchAndHandleErrors
import dev.fritz2.core.Handler
import dev.fritz2.core.invoke
import dev.fritz2.core.storeOf
import dev.fritz2.headless.validation.ComponentValidationMessage
import dev.fritz2.remote.btoa
import dev.fritz2.validation.ValidatingStore
import dev.fritz2.validation.valid
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.Flow

class RegistrationUserStore(job: Job) :
    ValidatingStore<RegistrationUser, Unit, ComponentValidationMessage>(
        initialData = RegistrationUser(),
        validation = RegistrationUser.validation,
        metadataDefault = Unit,
        validateAfterUpdate = false,
        job = job
    ) {

    private val client = RegistrationClient()

    private val progressStore = storeOf(RegistrationStep.USER3, job)
    val currentStep: Flow<RegistrationStep> = progressStore.data

    /**
     * Handler Schritt 3: handleUserData
     * Der Benutzer hat den Maillink mit dem Token geclickt, somit seine Mailadresse bestätigt und wurde dann
     * weitergeleitet zur Seite der Eingabe von Benutzername und Passwort. Der User hat die Eingabe abgeschickt.
     *
     * Wenn alle Daten gültig sind, wird [saveUserData] aufgerufen. Bei Erfolg wird eine Erfolgsmeldung im UI
     * angezeigt und der Store mit einer leeren Registration belegt. Andernfalls wird ein Fehler im UI angezeigt
     * und die aktuelle Registration behalten.
     */
    val handleUserData: Handler<Unit> = handle { registration ->

        GlobalServiceMessagesStore.clear()

        if (!validate(registration).valid)
            return@handle registration

        val success = globalSubmitTracker.track {
            saveUserData(registration)
        }

        if (success) {
            progressStore.update(RegistrationStep.SUCCESS4)
        } else return@handle registration

        // Registration beendet, leite auf Login-Seite weiter
        window.setTimeout(
            { Pages.root.visit() },
            redirectDelay
        )

        RegistrationUser()
    }

    /**
     * saveUserData: Wird in Schritt 3 des Registrierungsprozesses von [handleUserData] aufgerufen
     *
     * Diese Funktion sendet [registration] an den Server, der zu diesem Zeitpunkt auch den gewählten USername und 2
     * identische Passwörter enthält. Der Server versucht, die Benutzerdaten als neuen Useraccount zu speichern.
     *
     * @param registration Das vom Benutzer übermittelte Registrierungsobjekt, das an den Server gesendet werden soll
     *
     * @return False bei jedem Fehler, sonst true
     */
    private suspend fun saveUserData(registration: RegistrationUser): Boolean {
        return tryCatchAndHandleErrors("Schritt 3 der Registrierung") {
            client.createUser(
                registration.copy(
                    password = btoa(registration.password),
                    passwordRepeat = btoa(registration.passwordRepeat)
                )
            )
        } != null
    }
}